import { QueryCacheKey, QuerySubState, SubscriptionState } from '@reduxjs/toolkit/dist/query/core/apiState';
import { PatchCollection, UpdateQueryDataThunk } from '@reduxjs/toolkit/dist/query/core/buildThunks';
import { ApiEndpointMutation, ApiEndpointQuery, coreModuleName } from '@reduxjs/toolkit/dist/query/core/module';
import {
  EndpointBuilder,
  MutationDefinition,
  QueryDefinition,
  QueryExtraOptions,
  QueryFulfilledRejectionReason,
  ReplaceTagTypes
} from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import { reactHooksModuleName } from '@reduxjs/toolkit/dist/query/react/module';
import { Api, BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react';
import { RootState, store } from '../../app/store';
import { PaginatedResult } from '../../models';
import RequestService, { getNextPageURL } from '../services/requestService';
import { TEntity, tEntityId } from '../../models/entity';
import { MutationHooks, QueryHooks } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { FilterQueries } from '../../app/utils/filter';
import { isSearchArgsCheck, provideTagList } from './queryApiService';
import { SerializedError, ThunkAction } from '@reduxjs/toolkit';
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { setPropValue } from '../../app/utils/objectUtils';
import { PromiseWithKnownReason } from '@reduxjs/toolkit/dist/query/core/buildMiddleware/types';
import { miniSerializeError } from '../../app/utils/reducerUtils';


export interface FetchArg<T> {
  param?: PageParam & SortParam,
  query?: string | T,
  light?: boolean,
  disablePagination?: boolean,
  filter?: FilterQueries,
  skip?: boolean
}

export interface SearchQueries<T> extends FetchArg<T> {
  value: T | Partial<T>;
  filter?: FilterQueries;
  disablePagination?: boolean;
}

export type PageParam = { top?: number, skip?: number, disablePagination?: boolean, }
export type SortParam = { orderColumn?: string; ascending?: boolean; }
export interface ArgFilter<T> extends FetchArg<T> { filter?: FilterQueries; }
export type FilterSearchArg = { query: string; filter?: FilterQueries; };


export type ApiType<T extends TEntity, U extends string, E extends TEntityDefinitions<T, U>> = Api<TBaseQueryFn<any>, E, 'api', any, typeof coreModuleName | typeof reactHooksModuleName>;

export interface TDefinitions<T extends TEntity, U extends string, tId extends tEntityId = T['id']> {
  fetch: QueryDefinition<FetchArg<T>, TBaseQueryFn<T>, '' | U, PaginatedResult<T>, string> & QueryExtraOptions<'' | U, PaginatedResult<T>, FetchArg<T>, TBaseQueryFn<T>, string>,
  getById: QueryDefinition<tId, TBaseQueryFn<T>, '' | U, T | null, string>,
  filter: QueryDefinition<ArgFilter<T>, TBaseQueryFn<T>, '' | U, PaginatedResult<T>, string>,
  search: QueryDefinition<{ q: (SearchQueries<T> | string), disablePagination?: boolean }, TBaseQueryFn<T>, '' | U, PaginatedResult<T>>,
  create: MutationDefinition<T, TBaseQueryFn<T>, '' | U, T, any>,
  update: MutationDefinition<T & Pick<T, 'id'>, TBaseQueryFn<T>, '' | U, T, any>,
  delete: MutationDefinition<T['id'], TBaseQueryFn<T>, '' | U, void, any>,
  deleteReport: QueryDefinition<tId, TBaseQueryFn<any>, '' | U, any, any>,
}


export interface TApiEndpoints<T extends TEntity, U extends string, tId extends tEntityId = T['id']> {
  fetch: ApiEndpointQuery<TDefinitions<T, U>[QueryAction.fetch], TEntityDefinitions<T, U>> & QueryHooks<TDefinitions<T, U>[QueryAction.fetch]>,
  filter: ApiEndpointQuery<TDefinitions<T, U>[QueryAction.filter], TEntityDefinitions<T, U>> & QueryHooks<TDefinitions<T, U>[QueryAction.filter]>,
  getById: ApiEndpointQuery<TDefinitions<T, U, tId>[QueryAction.getById], TEntityDefinitions<T, U>> & QueryHooks<TDefinitions<T, U, tId>[QueryAction.getById]>,
  deleteReport: ApiEndpointQuery<TDefinitions<T, U, tId>[QueryAction.deleteReport], TEntityDefinitions<T, U>> & QueryHooks<TDefinitions<T, U, tId>[QueryAction.deleteReport]>,
  search: ApiEndpointQuery<TDefinitions<T, U>[QueryAction.search], TEntityDefinitions<T, U>> & QueryHooks<TDefinitions<T, U>[QueryAction.search]>,
  create: ApiEndpointMutation<TDefinitions<T, U>[QueryAction.create], TEntityDefinitions<T, U>> & MutationHooks<TDefinitions<T, any>[QueryAction.create]>,
  update: ApiEndpointMutation<TDefinitions<T, U>[QueryAction.update], TEntityDefinitions<T, U>> & MutationHooks<TDefinitions<T, any>[QueryAction.update]>,
  delete: ApiEndpointMutation<TDefinitions<T, U>[QueryAction.delete], TEntityDefinitions<T, U>> & MutationHooks<TDefinitions<T, any>[QueryAction.delete]>,
  patchUpdate: ApiEndpointMutation<TDefinitions<T, U>[QueryAction.update], TEntityDefinitions<T, U>> & MutationHooks<TDefinitions<T, any>[QueryAction.update]>
  patchDelete: ApiEndpointMutation<TDefinitions<T, U>[QueryAction.delete], TEntityDefinitions<T, U>> & MutationHooks<TDefinitions<T, any>[QueryAction.delete]>
}

export interface PatchUpdateDefinition<T extends TEntity, U extends string> {
  patchUpdate: MutationDefinition<T & Pick<T, 'id'>, TBaseQueryFn<T>, '' | U, T, any>
}

interface PatchCacheProps<T extends TEntity> {
  updateQuery: TUpdateQueryDataThunk<T>,
  item: T & Pick<T, 'id'>,
  serviceName: string
}
interface PatchItemPropCacheProps<T extends TEntity> {
  updateQuery: TUpdateQueryDataThunk<T>,
  patch: ItemPropPatch;
  serviceName: string
}

export interface ItemPropPatch {
  id: string | number,
  propName: string,
  propValue: any
}

interface PatchUpdateProps<T extends TEntity, U extends string> {
  build: TEndpointBuilder<T, U>,
  updateQuery: TUpdateQueryDataThunk<T>,
  requestPromise: (item: T) => Promise<T>,
  serviceName: string,
  idKey?: keyof T,
}

interface PatchDeleteProps<T extends TEntity, U extends string> {
  build: TEndpointBuilder<T, U>,
  updateQuery: TUpdateQueryDataThunk<T>,
  requestPromise: (id: tEntityId) => Promise<void>,
  serviceName: string
}

export enum QueryAction {
  fetch = 'fetch',
  filter = 'filter',
  getById = 'getById',
  create = 'create',
  update = 'update',
  delete = 'delete',
  search = 'search',
  patchUpdate = 'patchUpdate',
  patchDelete = 'patchDelete',
  deleteReport = 'deleteReport'
}

enum TagIdOption {
  LIST = 'LIST',
  TREE_LIST = 'TREE_LIST',
  SEARCH_LIST = 'SEARCH_LIST',
  FILTER_LIST = 'FILTER_LIST'
}

export type SelectOptionsPropertyKeys<T> = {
  [P in keyof T]: T[P] extends string | number ? P : never;
}[keyof T];


export type TMappedEntity<T, U extends string> = {
  [Property in keyof T as `${string & Property}${Capitalize<U>}`]: T[Property]
};

export type TEntityDefinitions<T extends TEntity, U extends string> = TMappedEntity<TDefinitions<T, U> & PatchUpdateDefinition<T, U>, U>;


type TUpdateQueryDataThunk<T extends TEntity> = UpdateQueryDataThunk<ReplaceTagTypes<{}, string> & {
  [x: string]: QueryDefinition<any, TBaseQueryFn<T>, any, any, string>;
}, RootState>

type RequestPromise<T> = Promise<PaginatedResult<T>> | Promise<T> | Promise<T[]> | Promise<void>;
export type RequestReturnType<T> = PaginatedResult<T> | T | T[] | void;
export type TBaseQueryFn<T> = BaseQueryFn<QueryRequest<T>, any, SerializedError, {}, {}>;
type TEndpointBuilder<T extends TEntity, U extends string> = EndpointBuilder<TBaseQueryFn<T>, U | '', any>;
type QueryRequest<T> = { request: RequestPromise<T>; meta?: {} | undefined; };
export type QueryPromiseResult<TResult> = QueryReturnValue<TResult, SerializedError, any>;
export type QueryResult<TResult> = { data: TResult; error?: undefined; } | { error: SerializedError; data?: undefined; };

function clientBaseQuery<T extends TEntity>(): TBaseQueryFn<T> {
  return async args => {
    var res: QueryPromiseResult<any> = { data: undefined, error: undefined, meta: args.meta };
    try {
      return { ...res, data: await args.request };
    } catch (error) {
      if (typeof error === 'string')
        return { ...res, error: { message: error } as SerializedError };
      return { ...res, error: miniSerializeError(error as Error) };
    }
  };
}


export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: clientBaseQuery<any>(),
  endpoints: () => ({}),
  tagTypes: ['']
}) as Api<TBaseQueryFn<any>, {}, 'api', any, typeof coreModuleName | typeof reactHooksModuleName>;

export function createInjectedApi<T extends TEntity, U extends string>(apiService: RequestService<T, U>) {
  const api = baseApi
    .enhanceEndpoints({
      addTagTypes: [apiService.typeName, `${apiService.typeName}_${QueryAction.deleteReport}` as U],
    })
    .injectEndpoints({
      endpoints: (build) => {
        let apiEndpoints = getApiEndpoints<T, U>(build, apiService, apiService.idKey);
        return {
          [`${QueryAction.fetch}${apiService.capitalizeTypeName}`]: apiEndpoints.fetch,
          [`${QueryAction.filter}${apiService.capitalizeTypeName}`]: apiEndpoints.filter,
          [`${QueryAction.getById}${apiService.capitalizeTypeName}`]: apiEndpoints.getById,
          [`${QueryAction.create}${apiService.capitalizeTypeName}`]: apiEndpoints.create,
          [`${QueryAction.update}${apiService.capitalizeTypeName}`]: apiEndpoints.update,
          [`${QueryAction.delete}${apiService.capitalizeTypeName}`]: apiEndpoints.delete,
          [`${QueryAction.deleteReport}${apiService.capitalizeTypeName}`]: apiEndpoints.deleteReport,
          [`${QueryAction.search}${apiService.capitalizeTypeName}`]: apiEndpoints.search,
        };
      }
    });
  return api;
}



export function getApiEndpoints<T extends TEntity, U extends string>(build: TEndpointBuilder<T, U>, apiService: RequestService<T, U>, idKey?: keyof T): TDefinitions<T, U> {

  return {
    fetch: build.query<PaginatedResult<T>, FetchArg<T>>({
      query: ({ filter, param, query, disablePagination, light }: FetchArg<T>) => {
        if (filter?.parameters?.type && typeof query !== 'object')
          return ({
            request: apiService.filter(filter, undefined,
              getNextPageURL({ endpoint: `${apiService.typeName}/filter`, param, disablePagination, query }))
          });

        if (query && typeof query === 'object') {
          return ({
            request: apiService.search({ value: query, filter } as SearchQueries<T>,
              param?.disablePagination,
              getNextPageURL({ endpoint: `${apiService.typeName}/search`, param })), meta: query
          });
        }
        return ({
          request: apiService
            .fetch(apiService.getNextPage({ param, query, disablePagination, light }))
        });
      },
      providesTags: (result, error, args) => {
        const additionalIdList: string[] = [];
        if (isSearchArgsCheck(args)) additionalIdList.push(TagIdOption.SEARCH_LIST);
        if (args.filter?.parameters != null) additionalIdList.push(TagIdOption.FILTER_LIST);
        return provideTagList(result?.value, apiService.typeName, additionalIdList, idKey);
      },
      keepUnusedDataFor: 60
    }),

    getById: build.query<T | null, T['id']>({
      query: (id: T['id']) => {
        return { request: apiService.getById(id) };
      },
      providesTags: (result, error, arg) => {
        return [
          { type: apiService.typeName, id: arg }
        ];
      }
    }),

    search: build.query<PaginatedResult<T>, { q: (SearchQueries<T> | string), disablePagination?: boolean }>({
      query: (arg) => {
        if (typeof arg.q === 'object') {
          return ({ request: apiService.search(arg.q, arg.disablePagination), meta: arg.q });
        }
        return ({ request: apiService.plainSearch(arg.q as string, arg.disablePagination), meta: arg.q });
      },
      providesTags: (result) => provideTagList(result?.value, apiService.typeName, [TagIdOption.SEARCH_LIST], idKey),
    }),

    filter: build.query<PaginatedResult<T>, ArgFilter<T>>({
      query: (query: ArgFilter<T>) => {
        if (typeof query.filter === 'object' && query.filter.parameters && query.filter.parameters.type) {
          return ({ request: apiService.filter(query.filter, typeof query === 'string' ? query : '', undefined, query.disablePagination), meta: query });
        }
        return ({ request: apiService.fetch(query?.param ? apiService.getNextPage({ param: query.param }) : undefined) });
      },
      providesTags: (result) => provideTagList(result?.value, apiService.typeName, [TagIdOption.FILTER_LIST], idKey),
    }),

    create: build.mutation<T, T>({
      query: (item: T) => ({ request: apiService.create(item), meta: item.id }),
      invalidatesTags: (resp, err, arg) => [
        { type: apiService.typeName, id: TagIdOption.LIST },
        { type: apiService.typeName, id: TagIdOption.SEARCH_LIST },
        { type: apiService.typeName, id: TagIdOption.FILTER_LIST }
      ]
    }),

    update: build.mutation<T, T & Pick<T, 'id'>>({
      query: (item) => {
        console.info('update mutation', item);
        return ({ request: apiService.update(item), meta: item.id });
      },
      invalidatesTags: (resp, err, arg) => {
        console.info('update mutation invalidates tags', resp, err, arg);
        return [
          { type: apiService.typeName, id: TagIdOption.LIST },
          { type: apiService.typeName, id: TagIdOption.SEARCH_LIST },
          { type: apiService.typeName, id: idKey ? arg[idKey] as tEntityId : arg.id },
          { type: apiService.typeName, id: TagIdOption.FILTER_LIST }
        ];
      }
    }),

    deleteReport: build.query<Response, T['id']>({
      query: (id: T['id']) => ({ request: apiService.deleteReport(id) }),
      providesTags: (result, error, arg) => [{ type: apiService.typeName, id: arg }, { type: `${apiService.typeName}_${QueryAction.deleteReport}` as U, id: arg }]
    }),

    delete: build.mutation<void, tEntityId>({
      query: (id: T['id']) => ({ request: apiService.delete(id) }),
      invalidatesTags: (resp, err, arg) => [
        { type: apiService.typeName, id: TagIdOption.LIST },
        // { type: apiService.typeName, id: arg },
        { type: apiService.typeName, id: TagIdOption.SEARCH_LIST },
        { type: apiService.typeName, id: TagIdOption.FILTER_LIST }
      ]
    }),

  };
}

export function patchItemIntoCache<T extends TEntity>({ updateQuery, item, serviceName }: PatchCacheProps<T>) {
  const queryCacheKey: QueryCacheKey[] = store.getState().api.provided[serviceName]?.[item.id];
  if (!queryCacheKey) return;
  queryCacheKey.forEach((cacheKey) => {
    const subState = store.getState().api.queries[cacheKey];
    let updateAction = (subState?.data as T)?.id ? getUpdateItemAction({ subState: subState as ItemQuerySubstate<T>, updateQuery, item }) : undefined;

    if (!updateAction && (subState?.data as PaginatedResult<T>).value) {
      updateAction = getUpdateListItemAction({ subState: subState as ListItemQuerySubstate<T>, updateQuery, item });
    }

    if (!updateAction) return;
    store.dispatch(updateAction);
  });
}

export function patchItemPropIntoCache<T extends TEntity>({ updateQuery, patch, serviceName }: PatchItemPropCacheProps<T>) {
  const queryCacheKey: QueryCacheKey[] = store.getState().api.provided[serviceName]?.[patch.id];
  if (!queryCacheKey) return;
  queryCacheKey.forEach((cacheKey) => {
    const subState = store.getState().api.queries[cacheKey];
    if (subState?.status === 'fulfilled') {
      let updateAction = (subState?.data as T)?.id ? getUpdateItemPropAction({ subState: subState as ItemQuerySubstate<T>, updateQuery, patch }) : undefined;

      if (!updateAction && (subState?.data as PaginatedResult<T>).value) {
        updateAction = getUpdateListItemPropAction({ subState: subState as ListItemQuerySubstate<T>, updateQuery, patch });
      }

      if (!updateAction) return;
      store.dispatch(updateAction);
    }

  });
}

type ItemQuerySubstate<T extends TEntity> = QuerySubState<QueryDefinition<string | number, TBaseQueryFn<T>, any, T, string>>;
type ListItemQuerySubstate<T extends TEntity> = QuerySubState<QueryDefinition<any, TBaseQueryFn<T>, any, PaginatedResult<T>, string>>;

function getUpdateItemAction<T extends TEntity>({ subState, updateQuery, item }:
  ({ item: T, subState: ItemQuerySubstate<T> | undefined, updateQuery: PatchCacheProps<T>['updateQuery'] })) {
  if (!subState || !subState.endpointName || !subState.data) return undefined;

  return updateQuery(subState.endpointName, subState?.originalArgs, (draft: T) => Object.assign(draft, item));
}

export function getUpdateItemPropAction<T extends TEntity>({ subState, updateQuery, patch }:
  ({ patch: ItemPropPatch, subState: ItemQuerySubstate<T> | undefined, updateQuery: PatchCacheProps<T>['updateQuery'] })) {
  if (!subState || !subState.endpointName || !subState.data) return undefined;

  return updateQuery(subState.endpointName, subState?.originalArgs, (draft: T) => Object.assign(draft, setPropValue(draft, patch.propName, patch.propValue)));
}

export function getUpdateListItemPropAction<T extends TEntity>({ subState, updateQuery, patch }:
  ({ patch: ItemPropPatch, subState: ListItemQuerySubstate<T>, updateQuery: PatchCacheProps<T>['updateQuery'] })) {
  if (!subState || !subState.endpointName || !subState.data) return undefined;

  return updateQuery(subState.endpointName, subState?.originalArgs,
    (draft: PaginatedResult<T>) => {
      const index = draft.value.findIndex(m => m.id === patch.id);
      if (index >= 0) {
        let item = draft.value[index];
        if (item) draft.value.splice(index, 1, setPropValue(item, patch.propName, patch.propValue));
      }
    });
}

function getUpdateListItemAction<T extends TEntity>({ subState, updateQuery, item }:
  ({ item: T, subState: ListItemQuerySubstate<T>, updateQuery: PatchCacheProps<T>['updateQuery'] })) {
  if (!subState || !subState.endpointName || !subState.data) return undefined;

  return updateQuery(subState.endpointName, subState?.originalArgs,
    (draft: PaginatedResult<T>) => {
      const index = draft.value.findIndex(m => m.id === item.id);
      if (index >= 0) {
        draft.value.splice(index, 1, item);
      }
    });
}

function getDeleteListItemAction<T extends TEntity>({ subState, updateQuery, id }:
  ({ id: string | number, subState: ListItemQuerySubstate<T>, updateQuery: PatchCacheProps<T>['updateQuery'] })) {
  if (!subState || !subState.endpointName || !subState.data) return undefined;

  return updateQuery(subState.endpointName, subState?.originalArgs as any, (draft: PaginatedResult<T>) => {
    const index = draft.value.findIndex(m => m.id === id);
    if (index >= 0) {
      draft.value.splice(index, 1);
    }
  });
}

function getQueryCacheKeys(itemId: string | number, serviceName: string) {
  const queryCache: QueryCacheKey[] = store.getState().api.provided[serviceName]?.[itemId];
  let patchResultCollection = [] as PatchCollection[];
  return {
    patchResultCollection,
    queryCache
  };
}

export function getPatchDeleteEndpoint<T extends TEntity, U extends string>({ updateQuery, requestPromise, build, serviceName }: PatchDeleteProps<T, U>) {
  return build.mutation<void, tEntityId>({
    query: (itemId) => ({ request: requestPromise(itemId) }),
    async onQueryStarted(itemId, { dispatch, queryFulfilled, getState, }) {
      const { queryCache, patchResultCollection } = getQueryCacheKeys(itemId, serviceName);
      queryCache.forEach((cacheKey) => {
        const subState = getState().api.queries[cacheKey];
        if (!subState?.endpointName) return;
        const deleteAction = (subState.data as PaginatedResult<T>).value ?
          getDeleteListItemAction({ id: itemId, updateQuery, subState: subState as ListItemQuerySubstate<T> })
          : undefined;
        if (!deleteAction) return;
        const patchResult = dispatch(deleteAction);
        patchResultCollection.push(patchResult);
      });

      await awaitQueryResult(queryFulfilled, patchResultCollection);
    },
    invalidatesTags: (resp, err, arg) => [
      { type: `${serviceName}_${QueryAction.deleteReport}` as U, id: arg },
      { type: serviceName as U, id: TagIdOption.SEARCH_LIST },
      { type: serviceName as U, id: TagIdOption.FILTER_LIST }]
  });
}

type TQueryResultFulfilled<TResult> = PromiseWithKnownReason<{
  data: TResult;
  meta: {} | undefined;
}, QueryFulfilledRejectionReason<TBaseQueryFn<any>>>


async function awaitQueryResult<TResult>(queryFulfilled: TQueryResultFulfilled<TResult>, patchCollection: PatchCollection[]) {
  try {
    const res = await queryFulfilled;
    return { success: true, ...res };
  } catch {
    patchCollection.forEach((patchResult) => patchResult.undo());
    return { success: false, data: undefined, meta: undefined };
  }
}

export function getPatchUpdateEndpoint<T extends TEntity, U extends string>(prop: PatchUpdateProps<T, U>) {
  return prop.build.mutation<T, T & Pick<T, 'id'>>({
    query: (item) => {
      return {
        request: prop.requestPromise(item),
      };
    },
    async onQueryStarted(item, { dispatch, queryFulfilled, getState }) {
      const id = prop.idKey ? item[prop.idKey] as tEntityId : item.id;
      const queryCacheKey: QueryCacheKey[] = getState().api.provided[prop.serviceName]?.[id];
      let patchResultCollection = [] as PatchCollection[];
      const injectPatch = (patch: T & Pick<T, 'id'>) => {
        if (queryCacheKey) {
          for (const cacheKey of queryCacheKey) {
            const subState = getState().api.queries[cacheKey];
            if (!subState?.endpointName) return;

            const isEntity = (substate: QuerySubState<any>) => !!(substate.data as T)?.id;
            const isList = (substate: QuerySubState<any>) => !!(substate.data as PaginatedResult<T>)?.value;

            let updateAction: ReturnType<typeof prop.updateQuery> | undefined = undefined;
            if (isEntity(subState)) {
              const updateEntityAction = prop.updateQuery(subState.endpointName, subState?.originalArgs as any,
                (draft: T) => {
                  Object.assign(draft, patch);
                });
              updateAction = updateEntityAction;
            } else if (isList(subState)) {
              const updateListAction = prop.updateQuery(subState.endpointName, subState?.originalArgs as any, (draft: PaginatedResult<T>) => {
                const index = draft.value.findIndex(m => m.id === patch.id);
                if (index >= 0) {
                  draft.value.splice(index, 1, patch);
                }
              });
              updateAction = updateListAction;
            }

            if (!updateAction) return;

            const patchResult = dispatch(updateAction);
            patchResultCollection.push(patchResult);

          }
        }
      };
      injectPatch(item);
      const res = await awaitQueryResult(queryFulfilled, patchResultCollection);
      if (res.success && res.data) injectPatch(res.data);
    },
    invalidatesTags: (resp, err, arg) => {
      return [
        { type: `${prop.serviceName}_${QueryAction.deleteReport}` as U, id: prop.idKey ? arg[prop.idKey] as tEntityId : arg.id },
        { type: prop.serviceName as U, id: TagIdOption.SEARCH_LIST },
        { type: prop.serviceName as U, id: TagIdOption.FILTER_LIST }
      ];
    }
  });
}


export function getOptionsPlainSearchPromise<T extends TEntity>(apiEndpoint: TApiEndpoints<T, any>['search']) {
  return async (searchString: string, predicate?: (value: T) => boolean) => {
    const req = apiEndpoint.initiate({ q: searchString, disablePagination: true });
    const storeResult = await store.dispatch(req);
    const result = storeResult.data?.value ?? [];
    if (predicate) {
      return result.filter(predicate);
    }
    return result;
  };
}

export function getOptionsPromise<T extends TEntity>(apiEndpoint: ApiEndpointQuery<QueryDefinition<void, TBaseQueryFn<T>, any, T[], string>, TEntityDefinitions<T, any>>) {
  return async () => {
    const result = await store.dispatch(apiEndpoint.initiate());
    return result.data ?? [];
  };
}

